import {Component, OnInit} from '@angular/core';
import {PublicSubmitForm} from '../../sdk/interface/form/public-submit-form';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {AlertService} from '../../sdk/service/alert.service';
import {ROUTES} from '../../app.routes';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {catchError, switchMap} from 'rxjs/operators';
import {EMPTY, finalize, Observable} from 'rxjs';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {SyncValidators} from '../../sdk/validator/SyncValidators';
import {PublicService, UserRecovery} from 'web-angular-client';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';

@Component({
    selector: 'app-password-reset',
    templateUrl: './password-reset.component.html',
    styleUrls: ['./password-reset.component.scss'],
    standalone: false
})
export class PasswordResetComponent extends PublicSubmitForm implements OnInit
{
    tokenId;
    emailAddressDisplayed;
    userRecovery: UserRecovery;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected route: ActivatedRoute,
        protected authenticationService: AuthenticationService,
        protected publicService: PublicService,
        private ngxLoader: NgxUiLoaderService,
        protected environmentService: EnvironmentService,
    )
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);

        this.emailAddressDisplayed = '';
    }

    ngOnInit()
    {
        this.form = this.formBuilder.group({
            password: ['', [Validators.required, SyncValidators.validatePassword]],
            confirmPassword: ['', Validators.required]
        }, {validator: SyncValidators.validatePasswordMatch()});

        // display the email address in the form if the token is successfully validated and user details are retrieved
        this.validateTokenAndGetUserDetails().subscribe(
            userRecovery =>
            {
                this.userRecovery = userRecovery;
                this.emailAddressDisplayed = userRecovery.username;
            },
            () =>
            {
                this.debugOutput('Password change request not valid');
                this.alertService.error('Password change request not valid', true);
                this.router.navigate([ROUTES.PUBLIC.LOGIN.path]);
            });
    }

    submit(action): void
    {
        this.loading = true;
        this.ngxLoader.start();
        this.changePasswordAndLogin(this.f.password.value).pipe(finalize(() => this.ngxLoader.stop())).subscribe({
                next: () =>
                {
                    this.loading = false;
                    // successfully authenticated.
                    this.debugOutput('password change successful');
                    this.alertService.success(
                        'Password change for user ' + this.userRecovery.username + ' successful!',
                        true);
                    this.router.navigate([this.routes.SECURE.HOME.VIEW.path]);
                }, error: (error: HttpErrorResponse) =>
                {
                    this.debugOutput('Password reset failed', error);
                    if (error?.status === HttpStatusCode.TooManyRequests && error.error?.remainingSeconds)
                    {
                        this.alertService.lockout(error.error.errorMessage, error.error.remainingSeconds);
                    }
                    else if (error?.status === HttpStatusCode.TooManyRequests)
                    {
                        this.alertService.error(error.error.errorMessage, true);
                        this.router.navigate([ROUTES.PUBLIC.LOGIN.path]);
                    }
                    else
                    {
                        this.alertService.error('Password reset failed, please try again.', true);
                        this.router.navigate([ROUTES.PUBLIC.LOGIN.path]);
                    }
                }
            }
        );
    }

    /**
     * Will validate the token passed in as a query parameter to the URL, then attempt to retrieve the user details
     * associated with the token. These actions are performed sequentially once the observable is subscribed to. If
     * the token is invalid, an error message is displayed and the user is redirected to the login screen.
     */
    private validateTokenAndGetUserDetails(): Observable<UserRecovery>
    {
        return this.route.queryParams.pipe(switchMap(param =>
        {
            this.tokenId = param.tokenId;
            return this.publicService.getUserFromToken(param.tokenId);
        }));
    }

    /**
     * Updates the password for the user details associated with the password reset token and attempts to login to the
     * user account immediately afterward with the updated password. If auto-login fails due to an independent LOGIN
     * lockout, the user is redirected to the login page with a success message instead of a confusing lockout error.
     */
    private changePasswordAndLogin(password): Observable<void>
    {
        this.userRecovery.password = password;

        return this.publicService.updateUserFromToken(this.tokenId, this.userRecovery).pipe(switchMap(() =>
        {
            return this.authenticationService.login(this.userRecovery.username, this.userRecovery.password).pipe(
                catchError((loginError: HttpErrorResponse) =>
                {
                    if (loginError?.status === HttpStatusCode.TooManyRequests)
                    {
                        this.alertService.success(
                            'Password change for user ' + this.userRecovery.username
                            + ' successful! Please log in.',
                            true);
                        this.router.navigate([ROUTES.PUBLIC.LOGIN.path]);
                        return EMPTY;
                    }
                    throw loginError;
                })
            );
        }));
    }
}